import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertCircle, Bluetooth, Bot, Camera, CheckCircle2, Clipboard, Copy, DatabaseBackup, ExternalLink, FileText, HardDrive, LifeBuoy, Monitor, Network, Power, Printer, Radio, RefreshCw, Rocket, Server, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { MeshDeviceDto, MeshDeviceHealthDto, TerminalDiagnosticsDto, TerminalLogEntry } from '../../models';
import { getApiError, getMeshCentralDeviceHealth, getMeshCentralLogs, runMeshCentralDiagnostics } from '../../services/api';
import { Button } from '../ui/button';
import { StatusBadge } from '../ui/status-badge';
import { MeshPowerControl } from './MeshPowerControl';
import { MeshRemoteWorkspace } from './MeshRemoteWorkspace';
import { MeshSupportCenter } from './MeshSupportCenter';
import { MeshTonometerProvisioning } from './MeshTonometerProvisioning';
import { MeshExamPrinting } from './MeshExamPrinting';
import { MeshConfigurationEditor } from './MeshConfigurationEditor';
import { AiAssistantDrawer } from '../ai/AiAssistantDrawer';

type PanelTab = 'overview' | 'exams' | 'support' | 'tonometer' | 'remote' | 'commands' | 'diagnostics' | 'logs' | 'configuration' | 'control';
const tabs: { id: PanelTab; label: string; icon: typeof Activity }[] = [
  { id: 'overview', label: 'Обзор', icon: Server },
  { id: 'exams', label: 'Осмотры и печать', icon: Printer },
  { id: 'support', label: 'Поддержка', icon: LifeBuoy },
  { id: 'tonometer', label: 'Тонометр', icon: Bluetooth },
  { id: 'remote', label: 'Удалённый доступ', icon: Monitor },
  { id: 'commands', label: 'Команды', icon: Rocket },
  { id: 'diagnostics', label: 'Диагностика', icon: Activity },
  { id: 'logs', label: 'Логи', icon: FileText },
  { id: 'configuration', label: 'Конфиг', icon: DatabaseBackup },
  { id: 'control', label: 'Управление', icon: Power },
];

interface Props {
  device?: MeshDeviceDto;
  onSelectFirst?: () => void;
  onOpenDetails?: (device: MeshDeviceDto) => void;
  variant?: 'preview' | 'page' | 'workspace';
}

export const MeshDevicePanel = ({ device, onSelectFirst, onOpenDetails, variant = 'preview' }: Props) => {
  const [tab, setTab] = useState<PanelTab>('overview');
  const [diagnostics, setDiagnostics] = useState<TerminalDiagnosticsDto>();
  const [logs, setLogs] = useState<TerminalLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [health, setHealth] = useState<MeshDeviceHealthDto>();
  const [healthLoading, setHealthLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const loadHealth = useCallback(async (silent = false) => {
    if (!device) return;
    if (!silent) setHealthLoading(true);
    try { setHealth((await getMeshCentralDeviceHealth(device.nodeId)).data.data); }
    catch (e) { if (!silent) setError(getApiError(e)); }
    finally { if (!silent) setHealthLoading(false); }
  }, [device]);

  useEffect(() => {
    setTab('overview');
    setError('');
    setSuccess('');
    setDiagnostics(undefined);
    setLogs([]);
    setHealth(undefined);
  }, [device?.nodeId]);

  useEffect(() => {
    if (!device) return;
    loadHealth();
    const timer = window.setInterval(() => loadHealth(true), 30_000);
    return () => window.clearInterval(timer);
  }, [device, loadHealth]);

  const perform = async (action: () => Promise<unknown>, message: string, after?: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await action();
      if (after) await after();
      setSuccess(message);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runDiagnostics = async () => {
    if (!device) return;
    await perform(
      async () => {
        const result = (await runMeshCentralDiagnostics(device.nodeId)).data.data;
        setDiagnostics(result);
        await loadHealth(true);
      },
      'Диагностика собрана',
    );
  };

  const copyTechnicalInfo = async () => {
    if (!device) return;
    const payload = {
      collectedAt: new Date().toISOString(),
      device: { name: device.name, hostname: device.hostname, nodeId: device.nodeId, ip: device.ip, os: device.os, online: device.online },
      terminal: health ? { terminalId: health.device.terminalId, version: health.medscreenerVersion, lastConnectedAt: health.lastConnectedAt } : null,
      diagnostics: health?.diagnostics || diagnostics || null,
      signals: health?.signals || null,
      activeExam: health?.activeExam || null,
      recentErrors: health?.recentErrors || [],
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setSuccess('Техническая информация скопирована');
    } catch { setError('Браузер не разрешил копирование'); }
  };

  const loadLogs = async () => {
    if (!device) return;
    await perform(
      async () => setLogs((await getMeshCentralLogs(device.nodeId, 200)).data.data.logs),
      'Логи обновлены',
    );
  };

  if (!device) {
    return <aside className="sticky top-0 flex min-h-[460px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/70 p-8 text-center shadow-sm">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700"><Network className="h-7 w-7" /></span>
      <h2 className="mt-4 text-lg font-semibold">Выберите устройство</h2>
      <p className="mt-2 max-w-xs text-sm text-muted-foreground">Карточка откроет все read-only операции напрямую по MeshCentral Node ID.</p>
      {onSelectFirst && <Button className="mt-5" variant="outline" onClick={onSelectFirst}>Открыть первое</Button>}
    </aside>;
  }

  const Root = variant === 'preview' ? 'aside' : 'section';
  const fullWorkspace = variant !== 'preview';
  const visibleTabs = fullWorkspace ? tabs : tabs.filter((item) => !['control', 'exams'].includes(item.id));

  return <Root className={cn('overflow-hidden rounded-2xl border border-border bg-card shadow-lg', variant === 'preview' && 'sticky top-0', variant === 'workspace' && 'flex h-full min-h-0 flex-col rounded-none border-0 shadow-none')}>
    <div className={cn('relative shrink-0 overflow-hidden border-b border-border bg-gradient-to-br from-slate-950 to-cyan-950 text-white', variant === 'page' ? 'p-4 sm:p-5' : variant === 'workspace' ? 'px-5 py-3.5' : 'p-5')}>
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-400/15 blur-2xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/10"><Server className="h-5 w-5 text-cyan-200" /></span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <StatusBadge label={device.online ? 'Online' : 'Offline'} variant={device.online ? 'success' : 'default'} live={device.online} />
            {fullWorkspace && <Button size="sm" variant="outline" onClick={() => setAiOpen(true)} className="border-cyan-200/30 bg-cyan-300/10 text-cyan-100 hover:bg-cyan-300/20 hover:text-white"><Bot className="h-4 w-4" />Спросить ИИ</Button>}
            {variant === 'preview' && onOpenDetails && <Button size="sm" variant="outline" onClick={() => onOpenDetails(device)} className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"><ExternalLink className="h-4 w-4" />На страницу</Button>}
          </div>
        </div>
        <h2 className={cn('truncate font-semibold', variant === 'workspace' ? '-mt-8 ml-14 text-lg' : 'mt-3 text-xl', variant === 'page' && 'sm:text-2xl')}>{device.name || 'Без имени'}</h2>
        <p className={cn('mt-1 truncate font-mono text-xs text-slate-300', variant === 'workspace' && 'ml-14')}>{device.hostname || device.nodeId}</p>
      </div>
    </div>

    <div aria-live="polite" className={cn('px-4 pt-4', variant === 'workspace' && 'shrink-0')}>
      {error && <div role="alert" className="rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div>}
      {success && <div className="rounded-xl border border-success/20 bg-success/10 px-3 py-2 text-xs text-success">{success}</div>}
    </div>

    <div className={cn('shrink-0 border-b border-border px-4 pt-4', variant === 'workspace' && 'pt-2')}>
      <div className="flex gap-1 overflow-x-auto pb-3">
        {visibleTabs.map(({ id, label, icon: Icon }) => <button
          key={id}
          onClick={() => setTab(id)}
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            tab === id ? 'bg-cyan-50 text-cyan-800' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        ><Icon className="h-3.5 w-3.5" />{label}</button>)}
      </div>
    </div>

    <div className={cn('min-h-[360px] p-4', variant === 'preview' ? 'max-h-[calc(100vh-250px)] overflow-y-auto' : 'sm:p-5', variant === 'workspace' && 'min-h-0 flex-1 overflow-y-auto')}>
      {tab === 'overview' && <CompactOverview device={device} health={health} loading={healthLoading} onRefresh={() => loadHealth()} onCopy={copyTechnicalInfo} />}

      {tab === 'diagnostics' && <div className="space-y-4">
        <div className="flex items-center justify-between gap-3"><div>
          <h3 className="font-semibold">Read-only диагностика</h3>
          <p className="text-xs text-muted-foreground">RAM, диск, uptime, сеть и сервис Telemed.</p>
        </div><div className="flex gap-2"><Button size="sm" onClick={runDiagnostics} disabled={busy || !device.online}><Activity className="h-4 w-4" />Запустить</Button>{diagnostics && <Button size="sm" variant="ghost" onClick={() => setDiagnostics(undefined)} className="text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" />Очистить</Button>}</div></div>
        {!device.online && <Hint text="Диагностика доступна только для online-устройства." />}
        {diagnostics ? <>
          <div className="grid grid-cols-2 gap-2">
            <MiniMetric label="Telemed" value={diagnostics.telemedStatus || '—'} />
            <MiniMetric label="Uptime" value={formatDuration(diagnostics.uptimeSeconds)} />
            <MiniMetric label="Диск" value={diagnostics.disk ? String(diagnostics.disk.usedPercent) + '%' : '—'} />
            <MiniMetric label="RAM" value={diagnostics.memory ? formatBytes(diagnostics.memory.usedBytes) + ' / ' + formatBytes(diagnostics.memory.totalBytes) : '—'} />
          </div>
          <InfoRows rows={[
            ['Базовый путь', diagnostics.basePathStatus || '—'],
            ['Версия Telemed', diagnostics.telemedVersion || '—'],
            ['Собрано', formatDate(diagnostics.collectedAt)],
          ]} />
        </> : <Empty icon={Activity} text="Диагностика ещё не собрана." />}
      </div>}

      {tab === 'logs' && <div className="space-y-4">
        <div className="flex items-center justify-between gap-3"><div>
          <h3 className="font-semibold">journalctl</h3>
          <p className="text-xs text-muted-foreground">Последние 200 строк напрямую с устройства.</p>
        </div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={loadLogs} disabled={busy || !device.online}><RefreshCw className={cn('h-4 w-4', busy && 'animate-spin')} />Получить</Button>{logs.length > 0 && <Button size="sm" variant="ghost" onClick={() => setLogs([])} className="text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" />Очистить</Button>}</div></div>
        {logs.length === 0 ? <Empty icon={FileText} text="Нажмите «Получить», чтобы загрузить логи." /> : <div className="max-h-[470px] overflow-auto rounded-xl bg-slate-950 p-3 font-mono text-[11px] leading-5 text-slate-200">
          {logs.map((line, index) => <div key={index} className="border-b border-white/5 py-1">
            <span className={cn('mr-2 font-bold', line.level === 'ERROR' ? 'text-rose-400' : line.level === 'WARN' ? 'text-amber-300' : 'text-cyan-300')}>{line.level}</span>
            <span className="break-all">{line.message}</span>
          </div>)}
        </div>}
      </div>}

      {tab === 'configuration' && <MeshConfigurationEditor device={device} />}

      {tab === 'remote' && fullWorkspace && <MeshRemoteWorkspace device={device} mode="remote" />}
      {tab === 'exams' && fullWorkspace && <MeshExamPrinting device={device} />}
      {tab === 'commands' && fullWorkspace && <MeshRemoteWorkspace device={device} mode="commands" />}
      {tab === 'support' && fullWorkspace && <MeshSupportCenter device={device} />}
      {tab === 'tonometer' && <MeshTonometerProvisioning device={device} />}

      {tab === 'control' && fullWorkspace && <MeshPowerControl device={device} />}
    </div>
    <AiAssistantDrawer open={aiOpen} onClose={() => setAiOpen(false)} nodeId={device.nodeId} />
  </Root>;
};

const InfoRows = ({ rows }: { rows: string[][] }) => <dl className="divide-y divide-border rounded-xl border border-border">{rows.map(([label, value]) => <div key={label} className="flex justify-between gap-4 px-3 py-2.5 text-xs"><dt className="shrink-0 text-muted-foreground">{label}</dt><dd className="min-w-0 break-all text-right font-mono text-foreground">{value}</dd></div>)}</dl>;
type HealthState = 'ok' | 'warning' | 'error' | 'unknown';
const overallHealth = (health: MeshDeviceHealthDto): HealthState => {
  if (!health.device.online || Object.values(health.signals).some((item) => item.state === 'error')) return 'error';
  if (Object.values(health.signals).some((item) => item.state === 'warning')) return 'warning';
  if (Object.values(health.signals).every((item) => item.state === 'unknown')) return 'unknown';
  return 'ok';
};
const healthCopy: Record<HealthState, { label: string; classes: string }> = {
  ok: { label: 'Норма', classes: 'bg-success/10 text-success' },
  warning: { label: 'Требует проверки', classes: 'bg-warning/10 text-warning' },
  error: { label: 'Требует внимания', classes: 'bg-destructive/10 text-destructive' },
  unknown: { label: 'Нет данных', classes: 'bg-muted text-muted-foreground' },
};
const HealthBadge = ({ state }: { state: HealthState }) => <span className={cn('inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold', healthCopy[state].classes)}>{healthCopy[state].label}</span>;

const CompactOverview = ({ device, health, loading, onRefresh, onCopy }: { device: MeshDeviceDto; health?: MeshDeviceHealthDto; loading: boolean; onRefresh: () => void; onCopy: () => void }) => <div className="space-y-3">
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3">
    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
      <div className="flex items-center gap-2"><h3 className="font-semibold">Состояние</h3>{health && <HealthBadge state={overallHealth(health)} />}</div>
      <span className="text-xs text-muted-foreground">В сети: {health?.lastConnectedAt ? formatDate(health.lastConnectedAt) : device.online ? 'сейчас online' : '—'}</span>
      <span className="text-xs text-muted-foreground">Medscreener: {health?.medscreenerVersion || '—'}</span>
    </div>
    <div className="flex gap-2"><Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}><RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />Обновить</Button><Button size="sm" variant="outline" onClick={onCopy}><Copy className="h-4 w-4" />Скопировать</Button></div>
  </div>

  <div className="grid gap-3 xl:grid-cols-3">
    <OverviewSection title="Связь и приложение">
      <CompactSignal icon={Network} title="API" signal={health?.signals.api} />
      <CompactSignal icon={Activity} title="Medscreener" signal={health?.signals.application} />
      <CompactSignal icon={Radio} title="STOMP" signal={health?.signals.stomp} />
      <div className="mt-2 grid grid-cols-2 gap-2"><Fact label="IP" value={device.ip || '—'} /><Fact label="Hostname" value={device.hostname || '—'} /></div>
    </OverviewSection>

    <OverviewSection title="Оборудование">
      <CompactSignal icon={Camera} title="Камера" signal={health?.signals.camera} />
      <CompactSignal icon={Bluetooth} title="Тонометр" signal={health?.signals.tonometer} />
      <CompactSignal icon={Printer} title="Принтер" signal={health?.signals.printer} />
      <CompactSignal icon={HardDrive} title="Диск" signal={health?.signals.disk} />
    </OverviewSection>

    <OverviewSection title="Требует внимания" danger={Boolean(health && overallHealth(health) === 'error')}>
      {!device.online && <AttentionRow icon={Network} title="Терминал offline" detail="MeshCentral не видит соединение" />}
      {health?.activeExam && <AttentionRow icon={Clipboard} title={`Осмотр №${health.activeExam.id} не завершён`} detail={formatDate(health.activeExam.startedAt)} />}
      {health?.recentErrors.slice(0, 2).map((item) => <AttentionRow key={item.key} icon={AlertCircle} title={item.title} detail={item.errorMessage || formatDate(item.updatedAt)} />)}
      {health?.diagnosticsError && <AttentionRow icon={AlertCircle} title="Терминал не ответил на диагностику" detail={health.diagnosticsError} />}
      {health && device.online && !health.activeExam && !health.recentErrors.length && !health.diagnosticsError && <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-success"><CheckCircle2 className="h-5 w-5" />Активных проблем нет</div>}
      {!health && <div className="flex min-h-24 items-center justify-center text-xs text-muted-foreground">Загружаем состояние…</div>}
    </OverviewSection>
  </div>

  <div className="grid gap-3 xl:grid-cols-[2fr_1fr]">
    <OverviewSection title="Последние действия">
      {health?.recentActions.length ? <div className="grid gap-2 md:grid-cols-3">{health.recentActions.slice(0, 3).map((item) => <div key={item.key} className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2"><div className="min-w-0"><p className="truncate text-xs font-semibold">{item.title}</p><p className="truncate text-[10px] text-muted-foreground">{formatDate(item.requestedAt)}</p></div><HealthBadge state={item.state === 'error' ? 'error' : item.state === 'active' ? 'warning' : 'ok'} /></div>)}</div> : <p className="py-3 text-center text-xs text-muted-foreground">Действий ещё не было</p>}
    </OverviewSection>
    <OverviewSection title="Технически">
      <div className="grid grid-cols-2 gap-2"><Fact label="Группа" value={device.groupName || '—'} /><Fact label="ОС" value={device.os || '—'} /><Fact label="Mesh Agent" value={`v${device.agentVersion ?? '—'}`} /><Fact label="Connection" value={String(device.connectionMask)} /></div>
    </OverviewSection>
  </div>
</div>;

const OverviewSection = ({ title, danger = false, children }: { title: string; danger?: boolean; children: React.ReactNode }) => <section className={cn('rounded-xl border p-3', danger ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-card')}><h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h4><div className="space-y-1.5">{children}</div></section>;
const CompactSignal = ({ icon: Icon, title, signal }: { icon: typeof Activity; title: string; signal?: MeshDeviceHealthDto['signals'][keyof MeshDeviceHealthDto['signals']] }) => {
  const state = signal?.state || 'unknown';
  return <div className="flex items-center gap-2 rounded-lg bg-muted/35 px-2.5 py-2"><span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', healthCopy[state].classes)}>{state === 'error' ? <AlertCircle className="h-3.5 w-3.5" /> : state === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}</span><div className="min-w-0 flex-1"><div className="flex items-center justify-between gap-2"><p className="text-xs font-semibold">{title}</p><span className={cn('text-[9px] font-bold', healthCopy[state].classes.split(' ').find((item) => item.startsWith('text-')))}>{healthCopy[state].label}</span></div><p className="truncate text-[10px] text-muted-foreground" title={signal?.detail}>{signal?.detail || 'Данных нет'}</p></div></div>;
};
const AttentionRow = ({ icon: Icon, title, detail }: { icon: typeof Activity; title: string; detail: string }) => <div className="flex gap-2 rounded-lg bg-destructive/5 px-2.5 py-2"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /><div className="min-w-0"><p className="truncate text-xs font-semibold">{title}</p><p className="truncate text-[10px] text-muted-foreground" title={detail}>{detail}</p></div></div>;
const Fact = ({ label, value }: { label: string; value: string }) => <div className="min-w-0 rounded-lg bg-muted/35 px-2.5 py-2"><p className="text-[9px] font-bold uppercase text-muted-foreground">{label}</p><p className="truncate font-mono text-[11px] font-semibold" title={value}>{value}</p></div>;
const MiniMetric = ({ label, value }: { label: string; value: string }) => <div className="rounded-xl border border-border bg-muted/35 p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 truncate text-sm font-semibold">{value}</p></div>;
const Hint = ({ text }: { text: string }) => <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{text}</div>;
const Empty = ({ icon: Icon, text }: { icon: typeof Activity; text: string }) => <div className="flex min-h-36 flex-col items-center justify-center rounded-xl border border-dashed border-border p-5 text-center"><Icon className="h-6 w-6 text-muted-foreground" /><p className="mt-2 text-xs text-muted-foreground">{text}</p></div>;
const formatBytes = (value?: number | null) => value == null ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value / 1073741824) + ' ГБ';
const formatDuration = (seconds?: number) => seconds == null ? '—' : Math.floor(seconds / 86400) + ' д ' + Math.floor((seconds % 86400) / 3600) + ' ч';
const formatDate = (value?: string | null) => value ? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '—';
